import { z } from "zod";

/** The perspective a conversation or question run is collected from. */
export const PerspectiveSchema = z.enum(["elev", "mentor", "vardnadshavare", "annan"]);
/** A concrete professional role used when the perspective is "annan". */
export const AnnanRoleSchema = z.enum([
  "specialpedagog",
  "kurator",
  "skolskoterska",
  "larare",
  "socialpedagog",
  "fritidspersonal",
  "annan_roll",
]);

export const PerspectiveRoleSchema = z.union([PerspectiveSchema.transform(() => undefined), AnnanRoleSchema]);

export const ConversationOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  symbol: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  altText: z.string().min(1).optional(),
  tone: z.enum(["positive", "neutral", "warning", "negative"]).optional(),
  isOther: z.boolean().optional(),
});

export const VisibilityRuleSchema = z.object({
  id: z.string().min(1).optional(),
  questionId: z.string().min(1),
  includesOptionId: z.string().min(1).optional(),
  answerKind: z.enum(["answered", "skipped"]).optional(),
});

export const SummaryCategorySchema = z.enum([
  "works",
  "gives_energy",
  "takes_energy",
  "hard",
  "important",
  "change",
  "understand",
  "other",
]);

export const SummaryMappingSchema = z.object({
  category: SummaryCategorySchema,
  label: z.string().min(1).optional(),
});

const QuestionBaseSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  shortTitle: z.string().min(1).optional(),
  supportText: z.string().min(1).optional(),
  professionalPrompt: z.string().min(1).optional(),
  suggestedFollowUps: z.array(z.string().min(1)).default([]),
  alternativeWording: z.array(z.string().min(1)).default([]),
  wordingByAudience: z.object({
    younger: z.string().min(1).optional(),
    older: z.string().min(1).optional(),
    teen: z.string().min(1).optional(),
    adult: z.string().min(1).optional(),
    enkel: z.string().min(1).optional(),
    standard: z.string().min(1).optional(),
  }).optional(),
  /** Optional per-perspective wording so the same item can be asked of elev, mentor, vårdnadshavare or another professional. */
  wordingByPerspective: z.object({
    elev: z.string().min(1).optional(),
    mentor: z.string().min(1).optional(),
    vardnadshavare: z.string().min(1).optional(),
    annan: z.string().min(1).optional(),
  }).optional(),
  theme: z.string().min(1),
  section: z.string().min(1).default("Övrigt"),
  required: z.literal(false).default(false),
  allowUnknown: z.boolean().default(true),
  allowDecline: z.boolean().default(true),
  allowSkip: z.boolean().default(true),
  priority: z.number().int().min(1).max(100).default(50),
  showWhen: VisibilityRuleSchema.optional(),
  summaryMapping: SummaryMappingSchema.default({ category: "other" }),
  safetyRuleId: z.string().min(1).optional(),
  otherPrompt: z.string().min(1).optional(),
  metric: z.object({
    key: z.string().regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/),
    label: z.string().min(1),
    direction: z.enum(["higher_is_better", "higher_is_more"]).default("higher_is_better"),
  }).optional(),
});

export const ChoiceQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("choice"), options: z.array(ConversationOptionSchema).min(2) });
export const YesNoQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("yes_no"), options: z.array(ConversationOptionSchema).min(2).max(3) });
export const MultiChoiceQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("multi_choice"), options: z.array(ConversationOptionSchema).min(2), maxSelections: z.number().int().positive().optional() });
export const ScaleQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("scale"), min: z.number().int(), max: z.number().int(), lowLabel: z.string().min(1), highLabel: z.string().min(1) });
export const FeelingQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("feeling"), options: z.array(ConversationOptionSchema).min(3).max(7) });
export const AgreementQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("agreement"), options: z.array(ConversationOptionSchema).min(3).max(7) });
export const ImageChoiceQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("image_choice"), options: z.array(ConversationOptionSchema.extend({ altText: z.string().min(1) })).min(2) });
export const EnergyQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("energy"), options: z.array(ConversationOptionSchema).min(3) });
export const RankingQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("ranking"), options: z.array(ConversationOptionSchema).min(2).max(8) });
export const TextQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("text"), placeholder: z.string().min(1).default("Skriv om du vill"), maxLength: z.number().int().min(1).max(2000).default(1000) });
export const TellQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("tell"), prompt: z.string().min(1).default("Du kan berätta med ord, peka eller visa på ditt sätt.") });
export const DrawingQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("drawing"), prompt: z.string().min(1).default("Rita om du vill.") });
export const PersonMapQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("person_map"), people: z.array(ConversationOptionSchema).min(1).max(12) });
export const PreviousAnswerQuestionSchema = QuestionBaseSchema.extend({ kind: z.literal("previous_answer"), sourceQuestionIds: z.array(z.string().min(1)).min(1), placeholder: z.string().min(1).default("Skriv om du vill lägga till något") });

export const ConversationQuestionSchema = z.discriminatedUnion("kind", [
  ChoiceQuestionSchema, YesNoQuestionSchema, MultiChoiceQuestionSchema, ScaleQuestionSchema, FeelingQuestionSchema,
  AgreementQuestionSchema, ImageChoiceQuestionSchema, EnergyQuestionSchema, RankingQuestionSchema,
  TextQuestionSchema, TellQuestionSchema, DrawingQuestionSchema, PersonMapQuestionSchema,
  PreviousAnswerQuestionSchema,
]);

export const ConversationTemplateSchema = z.object({
  id: z.string().min(1),
  version: z.number().int().positive(),
  locale: z.string().min(2).default("sv-SE"),
  title: z.string().min(1),
  description: z.string().min(1),
  icon: z.string().min(1),
  category: z.string().min(1).default("Övrigt"),
  audience: z.array(z.string().min(1)).min(1).default(["Alla"]),
  estimatedMinutes: z.number().int().positive().default(15),
  status: z.enum(["draft", "review", "published", "retired"]).default("published"),
  reviewedAt: z.string().date().optional(),
  reviewerRoles: z.array(z.string().min(1)).default([]),
  keywords: z.array(z.string().min(1)).default([]),
  /** Perspectives this template is designed to collect. Defaults to ["elev"] when absent. */
  perspectives: z.array(PerspectiveSchema).optional(),
  questions: z.array(ConversationQuestionSchema).min(1),
});

const ResponseBaseSchema = z.object({ questionId: z.string().min(1) });
const OtherTextSchema = z.string().trim().max(1000).optional();
const OptionResponseSchema = ResponseBaseSchema.extend({ kind: z.enum(["choice", "yes_no", "feeling", "agreement", "image_choice", "energy"]), optionId: z.string().min(1), otherText: OtherTextSchema });
export const MultiChoiceResponseSchema = ResponseBaseSchema.extend({ kind: z.literal("multi_choice"), optionIds: z.array(z.string().min(1)).min(1), otherText: OtherTextSchema });
export const ScaleResponseSchema = ResponseBaseSchema.extend({ kind: z.literal("scale"), value: z.number().int() });
export const RankingResponseSchema = ResponseBaseSchema.extend({ kind: z.literal("ranking"), optionIds: z.array(z.string().min(1)).min(2) });
export const TextResponseSchema = ResponseBaseSchema.extend({ kind: z.enum(["text", "tell", "previous_answer"]), text: z.string().max(2000) });
export const DrawingResponseSchema = ResponseBaseSchema.extend({ kind: z.literal("drawing"), dataUrl: z.string().startsWith("data:image/png;base64,").max(2_000_000) });
export const PersonMapResponseSchema = ResponseBaseSchema.extend({ kind: z.literal("person_map"), placements: z.record(z.string().min(1), z.number().int().min(1).max(3)) });
export const SkippedResponseSchema = ResponseBaseSchema.extend({ kind: z.literal("skipped"), reason: z.enum(["unknown", "declined", "skipped"]) });

export const ParticipantResponseSchema = z.discriminatedUnion("kind", [
  OptionResponseSchema, MultiChoiceResponseSchema, ScaleResponseSchema, RankingResponseSchema,
  TextResponseSchema, DrawingResponseSchema, PersonMapResponseSchema, SkippedResponseSchema,
]);

export type ConversationOption = z.infer<typeof ConversationOptionSchema>;
export type ConversationQuestion = z.infer<typeof ConversationQuestionSchema>;
export type ConversationTemplate = z.infer<typeof ConversationTemplateSchema>;
export type ParticipantResponse = z.infer<typeof ParticipantResponseSchema>;
export type SummaryCategory = z.infer<typeof SummaryCategorySchema>;
export type Perspective = z.infer<typeof PerspectiveSchema>;
export type AnnanRole = z.infer<typeof AnnanRoleSchema>;
